import Phaser from 'phaser';
import {
  COLORS,
  QUALITY,
  RUN,
  SCORING,
  SPAWN,
  SPEED,
} from '../config/gameConfig';
import { TRAFFIC_SCALE } from '../config/traffic';
import { AudioManager } from '../managers/AudioManager';
import { BoostSystem } from '../systems/BoostSystem';
import { IntensitySystem } from '../systems/IntensitySystem';
import { LaneSystem } from '../systems/LaneSystem';
import { ScoringSystem } from '../systems/ScoringSystem';
import { SpawnSystem } from '../systems/SpawnSystem';
import type { HudState, ObstacleKind, RunResult } from '../types';
import { EventBus, Events } from '../utils/EventBus';
import { mulberry32 } from '../utils/rng';

interface WorldItem {
  sprite: Phaser.GameObjects.Sprite;
  lane: number;
  visualLane: number;
  kind: string;
  isObstacle: boolean;
  speedFactor: number;
  scoredNearMiss: boolean;
  alive: boolean;
  weaves: boolean;
  weaveToLane: number | null;
  weaveStarted: boolean;
}

export interface GameSceneData {
  skinId: string;
  isDaily: boolean;
  seed: number | null;
  durationSec: number;
  reducedEffects: boolean;
  soundEnabled: boolean;
  musicEnabled: boolean;
}

export class GameScene extends Phaser.Scene {
  private lanes!: LaneSystem;
  private scoring!: ScoringSystem;
  private boost!: BoostSystem;
  private spawner!: SpawnSystem;
  private intensity!: IntensitySystem;
  private audio!: AudioManager;
  private finalStretch = false;
  private lastBanner: string | null = null;

  private player!: Phaser.GameObjects.Sprite;
  private items: WorldItem[] = [];
  private roadGraphics!: Phaser.GameObjects.Graphics;
  private scenery!: Phaser.GameObjects.Group;
  private particles?: Phaser.GameObjects.Particles.ParticleEmitter;

  private roadOffset = 0;
  private speed: number = SPEED.base;
  private timeLeft: number = RUN.durationSec;
  private crashes = 0;
  private invulnMs = 0;
  private countdownLeft: number = RUN.countdownSec;
  private playing = false;
  private ended = false;
  private runStart = 0;
  private roadInset = 56;
  private dataCfg!: GameSceneData;
  private swipeX: number | null = null;
  private unsubs: Array<() => void> = [];
  private keys: {
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    a: Phaser.Input.Keyboard.Key;
    d: Phaser.Input.Keyboard.Key;
    space: Phaser.Input.Keyboard.Key;
    esc: Phaser.Input.Keyboard.Key;
    p: Phaser.Input.Keyboard.Key;
  } | null = null;

  constructor() {
    super('GameScene');
  }

  init(data: Partial<GameSceneData>): void {
    this.dataCfg = {
      skinId: data.skinId ?? 'nairobi-neon',
      isDaily: data.isDaily ?? false,
      seed: data.seed ?? null,
      durationSec: data.durationSec ?? RUN.durationSec,
      reducedEffects: data.reducedEffects ?? false,
      soundEnabled: data.soundEnabled ?? true,
      musicEnabled: data.musicEnabled ?? true,
    };
  }

  create(): void {
    this.cleanupBus();
    this.lanes = new LaneSystem(RUN.lanes, 1);
    this.scoring = new ScoringSystem();
    this.boost = new BoostSystem();
    this.intensity = new IntensitySystem(SCORING.heatScoreBonus);
    const rng =
      this.dataCfg.seed != null ? mulberry32(this.dataCfg.seed) : Math.random;
    this.spawner = new SpawnSystem(rng);
    this.audio = new AudioManager(
      this,
      this.dataCfg.soundEnabled,
      this.dataCfg.musicEnabled,
    );

    this.timeLeft = this.dataCfg.durationSec;
    this.crashes = 0;
    this.invulnMs = 0;
    this.countdownLeft = RUN.countdownSec;
    this.playing = false;
    this.ended = false;
    this.finalStretch = false;
    this.lastBanner = null;
    this.speed = SPEED.base;
    this.items = [];
    this.roadOffset = 0;
    this.runStart = this.time.now;

    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLORS.skyBottom);
    this.drawSky();
    this.roadGraphics = this.add.graphics();
    this.scenery = this.add.group();
    this.seedScenery();

    const skinKey = `matatu-${this.dataCfg.skinId}`;
    const fallbackKey = 'matatu-nairobi-legend';
    this.player = this.add.sprite(
      this.lanes.laneX(width, this.roadInset),
      height * 0.78,
      this.textures.exists(skinKey) ? skinKey : fallbackKey,
    );
    this.player.setDepth(20);
    this.player.setOrigin(0.5, 0.55);
    // Keep lane footprint readable (~0.55 of source 128px width).
    this.player.setScale(0.58);

    if (!this.dataCfg.reducedEffects) {
      this.particles = this.add.particles(0, 0, 'particle', {
        speed: { min: 40, max: 120 },
        lifespan: 420,
        quantity: 0,
        scale: { start: 1, end: 0 },
        emitting: false,
      });
      this.particles.setDepth(30);
    }

    this.setupInput();
    this.bindBus();
    this.emitHud();

    this.scale.on('resize', this.onResize, this);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.tweens.add({
      targets: this.player,
      y: this.player.y - 2,
      duration: 480,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private drawSky(): void {
    const g = this.add.graphics().setDepth(-5);
    const { width, height } = this.scale;
    g.fillStyle(COLORS.skyTop, 1);
    g.fillRect(0, 0, width, height * 0.18);
    g.fillStyle(COLORS.skyBottom, 1);
    g.fillRect(0, height * 0.18, width, height * 0.2);
  }

  private seedScenery(): void {
    const { width, height } = this.scale;
    for (let i = 0; i < 8; i += 1) {
      const left = this.rng() > 0.5;
      const key = this.rng() > 0.55 ? 'building' : this.rng() > 0.5 ? 'shop' : 'tree';
      const s = this.add.sprite(
        left ? 28 + this.rng() * 20 : width - 28 - this.rng() * 20,
        -this.rng() * height,
        key,
      );
      s.setScale(key === 'tree' ? 0.9 : 0.7 + this.rng() * 0.25);
      s.setDepth(1);
      s.setData('speed', 0.55 + this.rng() * 0.2);
      this.scenery.add(s);
    }
  }

  private rng(): number {
    return Math.random();
  }

  private setupInput(): void {
    if (this.input.keyboard) {
      this.keys = {
        left: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
        right: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
        a: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
        d: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
        space: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
        esc: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC),
        p: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.P),
      };
    }

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.swipeX = p.x;
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (this.swipeX == null) return;
      const dx = p.x - this.swipeX;
      this.swipeX = null;
      if (Math.abs(dx) < 28) return;
      if (dx < 0) this.moveLane(-1);
      else this.moveLane(1);
    });
  }

  private bindBus(): void {
    this.unsubs.push(
      EventBus.on(Events.LANE_LEFT, () => this.moveLane(-1)),
      EventBus.on(Events.LANE_RIGHT, () => this.moveLane(1)),
      EventBus.on(Events.BOOST, () => this.tryBoost()),
      EventBus.on(Events.REQUEST_PAUSE, () => this.pauseRun()),
      EventBus.on(Events.REQUEST_RESUME, () => this.resumeRun()),
      EventBus.on(Events.REQUEST_RESTART, () => this.scene.restart(this.dataCfg)),
      EventBus.on(Events.REQUEST_MENU, () => {
        this.shutdownSoft();
        this.scene.stop();
      }),
    );
  }

  private cleanupBus(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
  }

  private onVisibility = (): void => {
    if (document.hidden && this.playing && !this.ended) {
      this.pauseRun();
    }
  };

  private onResize = (): void => {
    const { width, height } = this.scale;
    this.player.setPosition(this.lanes.laneX(width, this.roadInset), height * 0.78);
  };

  update(_t: number, delta: number): void {
    if (this.ended) return;
    const dt = Math.min(delta, 50);

    if (!this.playing) {
      this.updateCountdown(dt);
      this.drawRoad(dt * 0.25);
      this.emitHud();
      return;
    }

    this.handleKeys();
    this.lanes.update(dt);
    this.boost.update(dt);
    this.invulnMs = Math.max(0, this.invulnMs - dt);

    this.timeLeft = Math.max(0, this.timeLeft - dt / 1000);
    this.intensity.update(dt, this.timeLeft, this.dataCfg.durationSec);
    this.finalStretch = this.intensity.checkFinalStretch(this.timeLeft, this.finalStretch);

    const snap = this.intensity.snapshot(this.finalStretch);
    this.scoring.setHeatMultiplier(snap.multiplier);
    if (snap.banner && snap.banner !== this.lastBanner) {
      this.lastBanner = snap.banner;
      this.audio.go();
      this.flashBanner(snap.banner);
    }

    const progress = 1 - this.timeLeft / this.dataCfg.durationSec;
    const eased = Math.pow(progress, SPEED.rampExponent);
    let scroll = SPEED.base + eased * (SPEED.max - SPEED.base);
    if (snap.rushActive) scroll += SPEED.rushBonus;
    if (this.finalStretch) scroll += SPEED.finalStretchBonus;
    this.speed = scroll * this.boost.speedMultiplier();

    this.scoring.addDistance((this.speed * dt) / 1000);

    const { width } = this.scale;
    this.player.x = this.lanes.laneX(width, this.roadInset);
    this.player.setAlpha(this.invulnMs > 0 ? (Math.floor(this.invulnMs / 80) % 2 ? 0.45 : 1) : 1);

    // Lane-change banking + boost pulse (procedural animation).
    const bank = (this.lanes.visualLane - this.lanes.lane) * -8;
    this.player.setAngle(bank);
    const baseScale = 0.58;
    if (this.boost.isActive) {
      this.player.setScale(baseScale * (1.04 + Math.sin(this.time.now / 40) * 0.02));
    } else {
      this.player.setScale(baseScale);
    }

    this.drawRoad(dt);
    this.updateScenery(dt);
    this.spawn(dt);
    this.updateItems(dt);
    this.emitHud();

    if (this.timeLeft <= 0) {
      this.finish('timer');
    }
  }

  private updateCountdown(dt: number): void {
    const prev = Math.ceil(this.countdownLeft);
    this.countdownLeft -= dt / 1000;
    const next = Math.ceil(this.countdownLeft);
    if (next !== prev && next >= 1) this.audio.countdown();
    if (this.countdownLeft <= 0) {
      this.playing = true;
      this.audio.go();
    }
  }

  private handleKeys(): void {
    if (!this.keys) return;
    if (Phaser.Input.Keyboard.JustDown(this.keys.left) || Phaser.Input.Keyboard.JustDown(this.keys.a)) {
      this.moveLane(-1);
    }
    if (Phaser.Input.Keyboard.JustDown(this.keys.right) || Phaser.Input.Keyboard.JustDown(this.keys.d)) {
      this.moveLane(1);
    }
    if (Phaser.Input.Keyboard.JustDown(this.keys.space)) this.tryBoost();
    if (Phaser.Input.Keyboard.JustDown(this.keys.esc) || Phaser.Input.Keyboard.JustDown(this.keys.p)) {
      this.pauseRun();
    }
  }

  private moveLane(dir: -1 | 1): void {
    if (!this.playing || this.ended) return;
    this.lanes.tryMove(dir);
  }

  private tryBoost(): void {
    if (!this.playing || this.ended) return;
    if (this.boost.tryActivate()) this.audio.boost();
  }

  private drawRoad(dt: number): void {
    const { width, height } = this.scale;
    this.roadOffset = (this.roadOffset + (this.speed * dt) / 16) % 48;
    const g = this.roadGraphics;
    g.clear();
    g.fillStyle(COLORS.roadside, 1);
    g.fillRect(0, 0, width, height);
    g.fillStyle(COLORS.asphalt, 1);
    g.fillRect(this.roadInset - 8, 0, width - (this.roadInset - 8) * 2, height);

    // Edge lines
    g.lineStyle(4, 0xffffff, 0.9);
    g.lineBetween(this.roadInset, 0, this.roadInset, height);
    g.lineBetween(width - this.roadInset, 0, width - this.roadInset, height);

    // Lane dashes
    const usable = width - this.roadInset * 2;
    const laneW = usable / RUN.lanes;
    g.lineStyle(3, COLORS.asphaltLine, 0.95);
    for (let i = 1; i < RUN.lanes; i += 1) {
      const x = this.roadInset + laneW * i;
      for (let y = -48 + this.roadOffset; y < height + 48; y += 48) {
        g.lineBetween(x, y, x, y + 22);
      }
    }

    if (this.boost.isActive) {
      g.lineStyle(2, 0x90e0ef, 0.35);
      for (let i = 0; i < 6; i += 1) {
        const x = this.player.x + (i - 2.5) * 10;
        g.lineBetween(x, this.player.y + 20, x, this.player.y + 70);
      }
    }

    // Rush / final stretch road tint for urgency.
    if (this.intensity.isRush || this.finalStretch) {
      g.fillStyle(this.finalStretch ? 0xff6b1a : 0xef476f, 0.06);
      g.fillRect(this.roadInset - 8, 0, width - (this.roadInset - 8) * 2, height);
    }
  }

  private updateScenery(dt: number): void {
    const { height } = this.scale;
    this.scenery.children.each((obj) => {
      const s = obj as Phaser.GameObjects.Sprite;
      const factor = (s.getData('speed') as number) || 0.6;
      s.y += (this.speed * factor * dt) / 1000;
      if (s.y > height + 80) {
        s.y = -80 - Math.random() * 120;
      }
      return true;
    });
  }

  private spawn(dt: number): void {
    if (this.items.filter((i) => i.alive).length >= SPAWN.maxSimultaneous) return;
    const inten = this.intensity.snapshot(this.finalStretch);
    const events = this.spawner.update(dt, this.dataCfg.durationSec, {
      rushActive: inten.rushActive,
      finalStretch: this.finalStretch,
      heatRatio: inten.heatRatio,
    });
    for (const ev of events) {
      this.spawnItem(
        ev.lane,
        ev.kind,
        ev.type === 'obstacle',
        ev.speedFactor,
        ev.weaves ?? false,
        ev.weaveToLane ?? null,
      );
    }
  }

  private spawnItem(
    lane: number,
    kind: string,
    isObstacle: boolean,
    speedFactor: number,
    weaves = false,
    weaveToLane: number | null = null,
  ): void {
    const { width } = this.scale;
    const texture = this.textureFor(kind, isObstacle);
    const sprite = this.add.sprite(
      this.lanes.laneX(width, this.roadInset, lane),
      -60,
      texture,
    );
    sprite.setDepth(10);
    sprite.setOrigin(0.5, 0.55);
    const scale =
      kind === 'pothole'
        ? 1.05
        : kind === 'cones'
          ? 0.95
          : (TRAFFIC_SCALE[texture] ?? 0.62);
    sprite.setScale(scale);
    this.items.push({
      sprite,
      lane,
      visualLane: lane,
      kind,
      isObstacle,
      speedFactor,
      scoredNearMiss: false,
      alive: true,
      weaves,
      weaveToLane,
      weaveStarted: false,
    });
  }

  private textureFor(kind: string, isObstacle: boolean): string {
    if (!isObstacle) return kind === 'bonus' ? 'bonus' : 'fare';
    switch (kind as ObstacleKind) {
      case 'slowCar': {
        const roll = Math.random();
        // Prefer characterful Kenyan-road vehicles over plain sedans.
        if (roll < 0.45) return 'rival-matatu';
        if (roll < 0.75) return 'car-probox';
        if (roll < 0.9) return 'car-pickup';
        return 'car-yellow';
      }
      case 'traffic': {
        const roll = Math.random();
        if (roll < 0.2) return 'rival-matatu';
        if (roll < 0.4) return 'car-probox';
        if (roll < 0.55) return 'car-pickup';
        if (roll < 0.68) return 'car-red';
        if (roll < 0.8) return 'car-blue';
        if (roll < 0.9) return 'car-suv';
        return 'car-yellow';
      }
      case 'pothole':
        return 'pothole';
      case 'cones':
        return 'cones';
      case 'boda':
        return 'boda';
      case 'truck':
        return 'truck';
      default:
        return 'car-red';
    }
  }

  private updateItems(dt: number): void {
    const { height, width } = this.scale;
    for (const item of this.items) {
      if (!item.alive) continue;
      const rel = this.speed * (0.55 + item.speedFactor * 0.45);
      item.sprite.y += (rel * dt) / 1000;

      // Mid-approach lane weave — starts once the car is on screen.
      if (
        item.weaves &&
        item.weaveToLane != null &&
        !item.weaveStarted &&
        item.sprite.y > height * 0.18
      ) {
        item.weaveStarted = true;
      }
      if (item.weaveStarted && item.weaveToLane != null && item.lane !== item.weaveToLane) {
        const dir = Math.sign(item.weaveToLane - item.visualLane);
        item.visualLane += dir * (dt / 280);
        if (
          (dir > 0 && item.visualLane >= item.weaveToLane) ||
          (dir < 0 && item.visualLane <= item.weaveToLane)
        ) {
          item.visualLane = item.weaveToLane;
          item.lane = item.weaveToLane;
        }
      } else {
        item.visualLane = item.lane;
      }

      item.sprite.x = this.lanes.laneX(width, this.roadInset, item.visualLane);
      if (item.weaveStarted && item.lane !== item.visualLane) {
        item.sprite.setAngle((item.weaveToLane! - item.lane) * 6);
      } else {
        item.sprite.setAngle(0);
      }

      if (item.sprite.y > height + 80) {
        item.alive = false;
        item.sprite.destroy();
        continue;
      }

      const dy = item.sprite.y - this.player.y;
      const sameLane = Math.round(item.visualLane) === this.lanes.lane;
      const closeX = Math.abs(item.sprite.x - this.player.x) < 34;

      if (item.isObstacle) {
        if (
          !item.scoredNearMiss &&
          !sameLane &&
          Math.abs(Math.round(item.visualLane) - this.lanes.lane) === 1 &&
          Math.abs(dy) < SCORING.nearMissDistance &&
          dy > -10
        ) {
          item.scoredNearMiss = true;
          const rush = this.intensity.isRush || this.finalStretch;
          const pts = this.scoring.addNearMiss(rush);
          const { styleBonus } = this.intensity.addNearMissHeat();
          const chainPts = this.scoring.registerStyleHit(styleBonus);
          this.audio.nearMiss();
          this.popup(`+${pts}`, item.sprite.x, item.sprite.y, '#06d6a0');
          if (chainPts > 0) {
            this.popup(`STYLE +${chainPts}`, this.player.x, this.player.y - 50, '#ff006e');
          }
        }

        if (this.invulnMs <= 0 && sameLane && closeX && Math.abs(dy) < 48) {
          this.hitObstacle();
          item.alive = false;
          item.sprite.destroy();
        }
      } else if (sameLane && closeX && Math.abs(dy) < 40) {
        const pts =
          item.kind === 'bonus' ? this.scoring.addBonus() : this.scoring.addFare();
        this.intensity.addFareHeat();
        this.audio.pickup();
        this.burst(item.sprite.x, item.sprite.y);
        this.popup(`+${pts}`, item.sprite.x, item.sprite.y - 10, '#ffd166');
        item.alive = false;
        item.sprite.destroy();
      }
    }
    this.items = this.items.filter((i) => i.alive);
  }

  private hitObstacle(): void {
    this.crashes += 1;
    this.scoring.breakCombo();
    this.intensity.breakStyle();
    this.invulnMs = RUN.invulnMs;
    this.audio.crash();
    if (!this.dataCfg.reducedEffects) {
      this.cameras.main.shake(160, QUALITY.shakeIntensity);
    }
    this.tweens.add({
      targets: this.player,
      angle: this.player.angle + (Math.random() > 0.5 ? 12 : -12),
      duration: 90,
      yoyo: true,
    });
    this.popup('CRASH!', this.player.x, this.player.y - 40, '#ef476f');
    if (this.crashes >= RUN.crashLimit) {
      this.finish('crashes');
    }
  }

  private flashBanner(text: string): void {
    const { width, height } = this.scale;
    const label = this.add
      .text(width / 2, height * 0.28, text, {
        fontFamily: 'Fredoka, Nunito, sans-serif',
        fontSize: '34px',
        color: '#ffd166',
        stroke: '#111',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(50)
      .setAlpha(0);
    this.tweens.add({
      targets: label,
      alpha: 1,
      scale: { from: 0.7, to: 1.05 },
      duration: 220,
      yoyo: true,
      hold: 900,
      onComplete: () => label.destroy(),
    });
  }

  private burst(x: number, y: number): void {
    if (!this.particles) return;
    const qty = this.dataCfg.reducedEffects ? QUALITY.particlesLow : QUALITY.particlesHigh;
    this.particles.emitParticleAt(x, y, qty);
  }

  private popup(text: string, x: number, y: number, color: string): void {
    const label = this.add
      .text(x, y, text, {
        fontFamily: 'Fredoka, Nunito, sans-serif',
        fontSize: '18px',
        color,
        stroke: '#111',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(40);
    this.tweens.add({
      targets: label,
      y: y - 36,
      alpha: 0,
      duration: 650,
      onComplete: () => label.destroy(),
    });
    EventBus.emit(Events.POPUP, { text, color });
  }

  private emitHud(): void {
    const snap = this.scoring.snapshot();
    const inten = this.intensity.snapshot(this.finalStretch);
    const hud: HudState = {
      score: snap.score,
      timeLeft: this.timeLeft,
      fares: snap.fares,
      crashes: this.crashes,
      crashLimit: RUN.crashLimit,
      boostReady: this.boost.isReady,
      boostActive: this.boost.isActive,
      boostRatio: this.boost.ratio,
      combo: snap.combo,
      distance: snap.distance,
      countdown: this.playing ? null : Math.max(0, Math.ceil(this.countdownLeft)),
      invulnerable: this.invulnMs > 0,
      heatRatio: inten.heatRatio,
      scoreMult: inten.multiplier,
      rushActive: inten.rushActive,
      finalStretch: this.finalStretch,
      styleStreak: inten.styleStreak,
      banner: inten.banner,
    };
    EventBus.emit(Events.HUD, hud);
  }

  private pauseRun(): void {
    if (this.ended || !this.playing) return;
    this.scene.pause();
    EventBus.emit(Events.PAUSE);
  }

  private resumeRun(): void {
    this.scene.resume();
    EventBus.emit(Events.RESUME);
  }

  private finish(endedBy: 'timer' | 'crashes'): void {
    if (this.ended) return;
    this.ended = true;
    this.playing = false;
    const snap = this.scoring.snapshot();
    const result: Omit<RunResult, 'bestScore' | 'isLocalDemo' | 'newAchievements'> = {
      score: snap.score,
      distance: snap.distance,
      fares: snap.fares,
      bonuses: snap.bonuses,
      nearMisses: snap.nearMisses,
      crashes: this.crashes,
      comboMax: snap.comboMax,
      missionComplete: snap.missionComplete,
      durationMs: this.time.now - this.runStart,
      endedBy,
      isDaily: this.dataCfg.isDaily,
      dailySeed: this.dataCfg.isDaily && this.dataCfg.seed != null ? String(this.dataCfg.seed) : null,
    };
    EventBus.emit(Events.GAME_OVER, result);
  }

  private shutdownSoft(): void {
    this.cleanupBus();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.scale.off('resize', this.onResize, this);
  }

  shutdown(): void {
    this.shutdownSoft();
  }
}
